import React from 'react'
import * as yup from 'yup'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import FlatTextField from '@components/FlatTextField'
import { useFormik } from 'formik'
import { useTranslation } from 'next-i18next'
import { useSchoolInfo } from '@api/common'
import ResponsiveButton from '@components/ResponsiveButton'

const validationSchema = yup.object().shape({
  institution_name: yup.string().required('Field is required'),
  student_count: yup.string().required('Field is required'),
})

function InfoStep1({ data, setData, handleNext }) {
  const { t, i18n } = useTranslation(['common', 'register'])
  const formik = useFormik({
    initialValues: {
      for_client: data?.for_client ?? false,
      institution_name: data?.institution_name ?? '',
      student_count: data?.student_count ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      setData((prevData) => ({ ...prevData, ...values }))
      handleNext()
      actions.setSubmitting(false)
    },
  })
  const schoolInfoQuery = useSchoolInfo(data.domain, data.token, i18n.language)
  const studentCounts = schoolInfoQuery?.data?.student_count ?? []
  return (
    <Grid
      container
      spacing={{ xs: 2, md: 5 }}
      onSubmit={formik.handleSubmit}
      component="form"
    >
      <Grid
        item
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.What_is_your_institution_name')}
          backgroundColor="neutral.main"
          size="large"
          name="institution_name"
          value={formik.values.institution_name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.institution_name && Boolean(formik.errors.institution_name)}
          helperText={formik.touched.institution_name && formik.errors.institution_name}
          inputProps={{ 'aria-label': t('common:Institution_name') }}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <FlatTextField
          fullWidth
          label={t('register:form.How_many_students_do_you_currently_have')}
          backgroundColor="neutral.main"
          size="large"
          name="student_count"
          value={formik.values.student_count}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.student_count && Boolean(formik.errors.student_count)}
          helperText={formik.touched.student_count && formik.errors.student_count}
          inputProps={{ 'aria-label': t('register:form.students_count') }}
          select
        >
          {studentCounts?.map((type, index) => (
            <MenuItem
              key={index}
              value={index}
            >
              {type}
            </MenuItem>
          ))}
        </FlatTextField>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <FormControl
          error={
            formik.touched.is_register_btn_optional &&
            Boolean(formik.errors.is_register_btn_optional)
          }
        >
          <FormControlLabel
            control={
              <Checkbox
                id="for_client"
                name="for_client"
                checked={formik.values.for_client}
                onChange={(event) => formik.setFieldValue('for_client', event.target.checked)}
                inputProps={{ 'aria-label': t('register:form.for_client') }}
              />
            }
            label={
              <Typography
                variant="body1"
                fontWeight={400}
                sx={{ fontSize: { xs: 13, md: 'inherit' } }}
                color="primary.main"
              >
                {t('register:form.I_am_setting_up_Blue_assembly_for_a_client')}
              </Typography>
            }
          />
          <FormHelperText>{formik.touched.for_client && formik.errors.for_client}</FormHelperText>
        </FormControl>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <ResponsiveButton
            id="info-step1-button"
            variant="contained"
            type="submit"
            onClick={formik.handleSubmit}
            disableElevation
            aria-label={t('common:next')}
          >
            {t('common:next')}
          </ResponsiveButton>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default InfoStep1
